import React, { useContext, useState } from 'react'
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form'
import { Button, Form, Popup } from 'semantic-ui-react'
import AssignFeedPage from './AssignFeedPage'
import EditableLabelInput from '~/components/EditableLabelInput'
import { formatNumber } from '~/utils/helpers'
import Phases from './Phases'
import { FeedPageContext } from '../..'
import { CheckClimaEl } from '../../../style'
import AssignPhases from './AssignPhases'
import InfoPopup from '~/components/InfoPopup'
import LinkPopup from '../LinkPopup'
import AddNewCowGroup from './AddNewCowGroup'
import { DRY_PERIOD_TYPE, LACTATING_PERIOD_TYPE } from '../../utils'
import { Nullable } from '~/types'
import HeiferPhases from './HeiferPhases'
import { CO2OptionsType } from '~/api/services/co2'

interface Props {
  milkOptions: CO2OptionsType
}

const ElevenStep: React.FC<Props> = ({ milkOptions }) => {
  const { feedPage } = useContext(FeedPageContext)

  const { control, setValue } = useFormContext()

  const [cowFeedGroupType, setCowFeedGroupType] = useState<Nullable<number>>(null)

  const watchPhaseEnabled = useWatch({ control, name: 'phaseFeeding' })
  const watchRaisesHeifers = useWatch({ control, name: 'raiseHeifersOnFarm' })

  const { fields, append, update, remove } = useFieldArray({
    control,
    name: 'feedGroups',
    keyName: 'key'
  })

  const feedGroups = useWatch({ control, name: 'feedGroups' })

  function addNewFoodGroup(data) {
    setCowFeedGroupType(null)
    append({
      ...data,
      cattleNumber: data.cowsNumber,
      disabled: false,
      coarseFeeds: [],
      concentratedFeeds: [],
      juicyFeeds: []
    })
    const index = data.type - 1
    update(index, { ...fields[index], cattleNumber: fields[index]['cattleNumber'] - data.cowsNumber })
  }

  function handleDeleteFeedGroup(index) {
    const feedGroup = feedGroups[index]
    const fixedFeedGroupIndex = feedGroup?.type - 1

    setValue(`feedGroups[${fixedFeedGroupIndex}].cattleNumber`, parseInt(feedGroups[fixedFeedGroupIndex].cattleNumber) + parseInt(feedGroup.cattleNumber))
    remove(index)
  }

  function renderCowPerformanceGroups(cowFeedGroups) {

    return cowFeedGroups.map((feedGroup, index) => {
      const cowsNrInRelToDaysGroup = feedGroup['cattleNumber']
      const cowFeedIndex = fields.findIndex(cowFeedGroup => cowFeedGroup['name'] === feedGroup.name)

      return <EditableLabelInput
        key={feedGroup?.id || feedGroup['name']}
        label={`${feedGroup['name']}`}
        value={formatNumber(cowsNrInRelToDaysGroup, 0)}
        unit={'Tiere'}
        disabled={Boolean(feedGroup['disabled']) || index < 1}
        onValueChange={(value) => update(cowFeedIndex, { ...feedGroup, cattleNumber: value })}
        onLabelChange={(value) => update(cowFeedIndex, { ...feedGroup, name: value })}
        handleDelete={() => handleDeleteFeedGroup(cowFeedIndex)}
      />
    })
  }

  function renderCowsGroups() {
    const lactatingFeedGroups = feedGroups.filter(cowFeedGroup => cowFeedGroup.type === LACTATING_PERIOD_TYPE)
    const dryPeriodFeedGroups = feedGroups.filter(cowFeedGroup => cowFeedGroup.type === DRY_PERIOD_TYPE)
    return <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
        <Form.Group>
          {renderCowPerformanceGroups(lactatingFeedGroups)}
        </Form.Group>
        <Popup
          content={'Weitere Leistungsgruppe auf Basis der Laktierende hinzufügen'}
          trigger={<Button
            icon='plus'
            disabled={lactatingFeedGroups.length === 4}
            type='button'
            style={{ color: 'white', fontSize: '0.9em', marginTop: 12 }}
            onClick={() => setCowFeedGroupType(LACTATING_PERIOD_TYPE)}
          />}
        />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
        <Form.Group>
          {renderCowPerformanceGroups(dryPeriodFeedGroups)}
        </Form.Group>
        <Popup
          content={'Weitere Leistungsgruppe auf Basis der Trockensteher hinzufügen'}
          trigger={<Button
            icon='plus'
            disabled={dryPeriodFeedGroups.length === 2}
            type='button'
            style={{ color: 'white', fontSize: '0.9em', marginTop: 12 }}
            onClick={() => setCowFeedGroupType(DRY_PERIOD_TYPE)}
          />}
        />
      </div>
    </>
  }

  function renderCowFeedGroups() {
    return <>
      <div style={{ width: 'fit-content' }}>
        <div className='subSection'>
          <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 5 }}>
            <div style={{ display: 'flex', gap: 5 }}>
              <div style={{ fontWeight: 'bold' }}>{'Leistungsgruppen Milchkühe'}</div>
              <InfoPopup
                content={'Bitte erstellen Sie hier Leistungsgruppen, sofern Sie die Herden noch weiter aufgeteilt haben. (z.B. Frisch- und Altmelker; Frühstrockensteher und Vorbereiter).'}
                position={'top center'}
                styleIcon={{ fontSize: '1.2em' }}
              />
              <LinkPopup
                content={'Rationsberechnungen'}
              />
            </div>
          </div>
        </div>
        <CheckClimaEl.StyledDivider />
      </div>
      {renderCowsGroups()}
    </>
  }

  function renderAssignFeeds() {
    return <AssignFeedPage
      milkOptions={milkOptions}
    />
  }

  function renderAddCowNewGroup() {
    return <AddNewCowGroup
      handleClose={() => setCowFeedGroupType(null)}
      isOpen={Boolean(cowFeedGroupType)}
      type={cowFeedGroupType}
      handleSubmit={(data) => addNewFoodGroup(data)}
      max={feedGroups[cowFeedGroupType ? cowFeedGroupType - 1 : 0].cattleNumber}
    />
  }

  function renderPhaseFeeding() {
    return <Phases />
  }

  function renderAssingPhaseModal() {
    return <AssignPhases />
  }

  function renderCalfFeedGroups() {
    return <div style={{ marginTop: '2em' }}>
      <div style={{ width: 'fit-content' }}>
        <div className='subSection'>
          <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 5 }}>
            <div style={{ display: 'flex', gap: 5 }}>
              <div style={{ fontWeight: 'bold' }}>{'Leistungsgruppe Färsen'}</div>
              <LinkPopup
                content={'Rationsberechnungen'}
              />
            </div>
          </div>
        </div>
        <CheckClimaEl.StyledDivider />
      </div>
      <HeiferPhases milkOptions={milkOptions} />
    </div>
  }

  return <Form.Field style={{ margin: '2em', marginTop: 0 }}>
    {renderAddCowNewGroup()}
    {feedPage === 0 && <>
      {renderCowFeedGroups()}
      {renderPhaseFeeding()}
      {Boolean(watchPhaseEnabled) && renderAssingPhaseModal()}
      {Boolean(watchRaisesHeifers) && renderCalfFeedGroups()}
    </>}
    {feedPage === 1 && renderAssignFeeds()}
  </Form.Field>
}
export default ElevenStep
